import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { styles } from './styles';
import { actions as productCatalogActions } from '../../../app/store/ducks/product-catalog.duck';
import { actions as categoriesActions } from '../../../app/store/ducks/categories.duck';
import { ReactComponent as MenuBtn } from '../../../_base/layout/assets/layout-svg-icons/menu-button.svg';
import { IAppState } from '../../../app/store/rootDuck';
import { Typography, useMediaQuery } from '@mui/material';
import { ICategoryItem } from '../../../app/pages/home/categories/interfaces';
import MoreItems from './MoreItems';
import { useModalCategory } from './categoryContext';

function splitIntoColumns(items: ICategoryItem[], columnsCount = 3) {
  const perColumn = Math.ceil(items.length / columnsCount);
  const columns = [];

  for (let i = 0; i < columnsCount; i++) {
    columns.push(items.slice(i * perColumn, (i + 1) * perColumn));
  }
  return columns;
}

export default function Categories() {
  const { classes } = styles();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { isOpen: show, close } = useModalCategory();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:1024px)');
  const [selectedCategories, setSelectedCategories] = React.useState<ICategoryItem[]>([]);
  const [rootCategory, setRootCategory] = React.useState<ICategoryItem | null>(null);
  const categories = useSelector((state: IAppState) => state.categories.catalogCategories);
  const onlyActiveCategories = categories.filter(i => i.active);

  const setSelected = (selected: ICategoryItem) => {
    if (selected.children?.length) {
      setSelectedCategories([...selectedCategories, selected]);
      containerRef.current?.scroll({
        top: 0,
        behavior: 'smooth',
      });
    } else {
      selected.id && handleItem(selected.id);
    }
  };
  const removeSelected = (selected: ICategoryItem) =>
    setSelectedCategories(selectedCategories.filter(i => i.id !== selected.id));
  const selectedCategory: ICategoryItem | undefined =
    selectedCategories[selectedCategories.length - 1];

  useEffect(() => {
    dispatch(
      categoriesActions.fetchCatalogCategories({ with_products: true, only_active: 1 })
    );
  }, []);

  const columns = splitIntoColumns(rootCategory?.children?.filter(i => i.active) || [], 3);

  const handleItem = (id: number) => {
    if (id) {
      dispatch(productCatalogActions.clearRequest());
      dispatch(productCatalogActions.setSearching(''));
      navigate('/products/catalog');
      dispatch(productCatalogActions.setCategoryIdActive(id));
      close();
    }
  };

  if (!show) return null;

  if (isMobile) {
    return (
      <div className={classes.container}>
        <div className={classes.contentMobile} ref={containerRef}>
          {selectedCategory ? (
            <>
              <div className={classes.containerSelectedCategory}>
                <div
                  className={classes.containerBackBtn}
                  onClick={() => removeSelected(selectedCategory)}
                >
                  <MenuBtn />
                </div>
                <div
                  className={classes.containerMobileTitle}
                  onClick={() => selectedCategory.id && handleItem(selectedCategory.id)}
                >
                  <Typography component='p' className={classes.rootMobileTitle}>
                    {selectedCategory.name}
                  </Typography>
                </div>
              </div>
              {selectedCategory.children?.map(i => (
                <div className={classes.containerSubCategory} key={i.id}>
                  <Typography
                    component='p'
                    className={classes.rootMobileTitle}
                    onClick={() => i.id && handleItem(i.id)}
                  >
                    {i.name}
                  </Typography>
                  {i.children?.map(j => (
                    <Typography
                      key={j.id}
                      component='p'
                      className={classes.rootMobileSubTitle}
                      onClick={() => setSelected(j)}
                    >
                      {j.name}
                    </Typography>
                  ))}
                </div>
              ))}
            </>
          ) : (
            <>
              {onlyActiveCategories.map(i => (
                <div
                  key={i.id}
                  className={classes.containerRootCategory}
                  onClick={() => setSelected(i)}
                >
                  <Typography component='p' className={classes.rootMobileTitle}>
                    {i.name}
                  </Typography>
                  <div className={classes.containerMenuBtn}>
                    <MenuBtn />
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.content}>
        <div className={classes.containerRoot}>
          {onlyActiveCategories.map(i => {
            return (
              <div
                key={i.id}
                className={classes.blockRoot}
                style={{
                  backgroundColor: rootCategory?.id === i.id ? '#f5f5f5' : '#fff',
                }}
                onMouseEnter={() => i.id && setRootCategory(i)}
                onClick={() => i.id && handleItem(i.id)}
              >
                <Typography
                  component='p'
                  className={classes.rootTitle}
                  style={{
                    color: rootCategory?.id === i.id ? '#4c4c4c' : '#282a3c',
                  }}
                >
                  {i.name}
                </Typography>
              </div>
            );
          })}
        </div>
        <div className={classes.containerChildren}>
          <Typography component='p' className={classes.title}>
            {rootCategory?.name}
          </Typography>

          <div className={classes.childrens}>
            {columns.map((col, i) => (
              <div className={classes.column} key={i}>
                {col.map((item, j) => {
                  if (!item.active) return null;
                  return <MoreItems key={j} item={item} handleItem={handleItem} />;
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
